import { callAPI } from 'shared/api/callApi';

/** The quarters the operator can pick in TIER SEARCH — the server fills all four even when a quarter shipped nothing. */
export const QUARTERS = [1, 2, 3, 4];

/**
 * Display format for PFL/PFG.
 *
 * The server already rounds to four decimals, so the screen never rounds again
 * (rounding twice stops the figures matching the quarterly totals). Only thousands
 * separators are added.
 *
 * null/undefined become an empty string, and that has to stay distinct from zero:
 * "0.0000" is the system stating the volume IS zero, while a blank cell states nothing.
 *
 * A null arrives for two reasons. The entry holds no soju at all, which is harmless — but it
 * also arrives when the item IS soju and its ml/alc was never registered on the item master,
 * so the server could not convert it. Printing zero there would tell the operator the year has
 * room left, they would file Tier 1, and the volume that was never counted could already have
 * passed the limit. It would also hide the missing registration, so the same entry repeats.
 * The cumulative figure is null for an entry with no import date too — accumulation is ordered
 * by import date, so an undated entry has no position in it, and a zero would read as
 * "first entry of the year" when filling the date in might drop it past 100,000.
 *
 * A blank makes the operator stop and check the item master or the broker's figure. Showing
 * what is not known beats showing a number that is wrong.
 */
export const formatProofVolume = (value) => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (!Number.isFinite(num)) return '';
    return num.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
};

/**
 * The one colour that means "this year is already past the Tier 1 limit".
 *
 * The operator meets this fact in two places — the YTD column on the entry list and the TIER
 * SEARCH badge and quarter rows — and has to be able to trust that both are talking about the
 * same thing. Both read the colour from here so they can never disagree.
 */
export const SOJU_OVER_LIMIT_COLOR = '#d32f2f';

/**
 * Whether an edit the operator just made changed how much soju the year has shipped.
 *
 * When it did, the YTD Soju PFG on the entry list is out of date for every entry of that year
 * and the list has to be read again; when only beer or promotional rows moved, nothing about the
 * limit changed and the operator is not made to wait for a re-read.
 *
 * Pass the row as it was and as it now is: a soju row the operator turned into another product
 * still took its volume out of the year. `pfg` is the server's own verdict on whether a row
 * carries soju volume — null means it does not, or could not be measured.
 */
export const touchesSojuVolume = (...rows) => rows.flat().some((row) => row?.pfg != null);

const TIER1_CODE = 'S01010';

/**
 * Whether the operator picked Tier 1 on this row.
 *
 * Going over the limit is only worth reporting when the operator **picked Tier 1**.
 * Even in a year already past the limit, choosing Tier 2 or beer is the right call, so there
 * is nothing to ask about.
 */
export const isTier1Code = (tier) => (tier ?? '').trim().toUpperCase() === TIER1_CODE;

/**
 * Reads the quarterly accumulation for a year.
 *
 * @param {{ year?: number|string }} params - leave year empty and the server picks the most
 *   recent year that has data.
 */
export const fetchSojuTierAccumulation = async ({ year, getAccessTokenSilently, userEmail }) => {
    const resp = await new Promise((resolve, reject) => {
        callAPI({
            method: 'get',
            module: 'duty_calculator',
            component: 'soju_tier_accumulation',
            type: year != null && year !== '' ? String(year) : null,
            getAccessTokenSilently,
            email: userEmail,
            onSuccess: (resp) => resolve(resp),
            onError: (error) => reject(error ?? new Error('Failed to load the soju tier accumulation.')),
        });
    });

    return resp?.data ?? null;
};

/**
 * Asks whether the year would go over the limit once the rows being saved are counted in.
 *
 * The server only reads id, item_code, quantity and tier, so only those four are sent.
 * When editing an already saved row the id must be included, or the server cannot take that
 * row out of the saved total and count it again with the new value.
 */
export const checkSojuTier = async ({
    ilDutyEntryId,
    importDate,
    details = [],
    getAccessTokenSilently,
    userEmail,
}) => {
    const payload = {
        il_duty_entry_detail: details.map((detail) => ({
            id: detail?.id,
            item_code: detail?.item_code,
            quantity: detail?.quantity,
            tier: detail?.tier ?? '',
        })),
    };
    if (ilDutyEntryId != null) payload.il_duty_entry_id = ilDutyEntryId;
    else if (importDate) payload.import_date = importDate;

    const resp = await new Promise((resolve, reject) => {
        callAPI({
            method: 'post',
            module: 'duty_calculator',
            component: 'soju_tier_check',
            getAccessTokenSilently,
            email: userEmail,
            obj: payload,
            onSuccess: (resp) => resolve(resp),
            onError: (error) => reject(error ?? new Error('Failed to check the soju tier limit.')),
        });
    });

    return resp?.data ?? null;
};

/** The confirmation the operator reads before saving over the limit — the server's sentence plus the Tier 1 rows it concerns. */
const buildAlertPrompt = (alert) => {
    const lines = (alert?.rows ?? []).map(
        (row) => `- ${row?.item_code ?? ''} x ${row?.quantity ?? ''} = ${formatProofVolume(row?.pfg)} PFG`,
    );
    return [alert?.message ?? '', ...lines].filter(Boolean).join('\n');
};

/**
 * Limit check run right before saving. Answers with a boolean: may the save go ahead?
 *
 * - alert is null — save without asking
 * - alert is present — ask the operator (the broker's judgement comes first, so confirming saves)
 * - the check itself fails — ask separately rather than passing over it silently, so that
 *   neither "saved without knowing it was over" nor "a broken check blocked the save" happens.
 *
 * @returns {Promise<boolean>}
 */
export const confirmSojuTierSelection = async ({
    ilDutyEntryId,
    importDate,
    details = [],
    getAccessTokenSilently,
    userEmail,
}) => {
    if (!details.some((detail) => isTier1Code(detail?.tier))) return true;

    let result;
    try {
        result = await checkSojuTier({ ilDutyEntryId, importDate, details, getAccessTokenSilently, userEmail });
    } catch (error) {
        console.error('[SojuTier] Failed to check the Tier 1 limit:', error);
        const reason = error?.message ?? 'unknown error';
        return window.confirm(`Could not check the soju Tier 1 limit (${reason}). Save without the check?`);
    }

    if (!result?.alert) return true;

    return window.confirm(buildAlertPrompt(result.alert));
};
